import dayjs from 'dayjs';
import i18next from 'i18next';

import { ICONS } from '../../constants/icons';
import { toMoment } from '../utils/dates';

export const vehicleTypesMap = {
  auto: {
    label: 'auto',
    value: 'auto',
    icon: 'car',
  },
  bus: {
    label: 'bus',
    value: 'bus',
    icon: 'bus',
  },
  van: {
    label: 'van',
    value: 'van',
    icon: 'van-shuttle',
  },
  ambulance: {
    label: 'ambulance',
    value: 'ambulance',
    icon: 'ambulance',
  },
};

// Categoria mezzo → token del registro icone. Un concetto = un glifo: le voci di
// `vehicleTypesMap` restano bare (`car`, `van-shuttle`) per retrocompatibilità
// con chi le compone a mano, qui si passa dal registro semantico.
const VEHICLE_CATEGORY_ICON = {
  auto: ICONS.car,
  bus: ICONS.bus,
  van: ICONS.vehicle,
  ambulance: ICONS.ambulance,
};

/** Classe FontAwesome della categoria di un mezzo (default: van-shuttle). */
export const vehicleCategoryIcon = (category) => VEHICLE_CATEGORY_ICON[category] || ICONS.vehicle;

/**
 * Posti del mezzo, risolti dalla tipologia assegnata.
 *
 * La tipologia è la fonte di verità — è lì che si dichiarano posti totali,
 * passeggeri e carrozzina — ma i campi legacy sul mezzo (`seats`,
 * `wheelchair_filling`, `max_wheelchair_seats`) restano compilati sui mezzi
 * censiti prima delle tipologie e su quelli a cui nessuna è stata assegnata.
 * Si legge quindi la tipologia quando c'è, il mezzo altrimenti: senza fallback
 * quelle righe mostrerebbero un vuoto al posto di dati che esistono.
 *
 * @returns {{total: number|null, wheelchair: number|null, from: 'type'|'vehicle'}}
 */
export function resolveVehicleSeats(vehicle, type) {
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

  if (type) {
    return {
      total: num(type.total_seats),
      wheelchair: num(type.wheelchair_slots),
      from: 'type',
    };
  }
  return {
    total: num(vehicle?.seats),
    // `wheelchair_filling` is how many seats one wheelchair takes, not how many
    // wheelchairs fit: the capacity is `max_wheelchair_seats`.
    wheelchair: num(vehicle?.max_wheelchair_seats),
    from: 'vehicle',
  };
}

/**
 * What the vehicle can carry: passenger seats (driver excluded) and wheelchair
 * places. The single reading for every capacity check — from the type when the
 * vehicle has one, from its own legacy fields otherwise.
 *
 * The legacy fields stay written on the vehicle (the backend pooling and the
 * drivers app read them): `capacityFields` is what a save must write there.
 *
 * @returns {{seats: number|null, wheelchairs: number|null, from: 'type'|'vehicle'}}
 */
export function vehicleCapacity(vehicle, type) {
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  if (type) {
    return {
      seats: num(type.passenger_seats),
      wheelchairs: num(type.wheelchair_slots),
      from: 'type',
    };
  }
  return {
    seats: num(vehicle?.seats),
    wheelchairs: num(vehicle?.max_wheelchair_seats),
    from: 'vehicle',
  };
}

/** Legacy capacity fields of a vehicle, as its type declares them. */
export const capacityFields = (type) => {
  const { seats, wheelchairs } = vehicleCapacity(null, type);
  return { seats: seats ?? 0, max_wheelchair_seats: wheelchairs ?? 0 };
};

/** The vehicle with its capacity fields resolved (see `vehicleCapacity`). */
export function withCapacity(vehicle, types = []) {
  const type = vehicle?.vehicle_type_id
    ? types.find((t) => t.id === vehicle.vehicle_type_id)
    : null;
  return type ? { ...vehicle, ...capacityFields(type) } : vehicle;
}

/** "8 posti · 2 carrozzina" — riga compatta per liste e carte. */
export function seatsSummary(vehicle, type, translate = (k, d) => d) {
  const { total, wheelchair } = resolveVehicleSeats(vehicle, type);
  const parts = [];
  if (total !== null) parts.push(`${total} ${translate('posti_short', 'posti')}`);
  if (wheelchair) parts.push(`${wheelchair} ${translate('posti_carrozzina_short', 'carrozzina')}`);
  return parts.join(' · ') || '—';
}

/**
 * Stato del mezzo. Registro unico nella forma degli altri di flotta
 * (`damagesLogic`, `driverStatusLogic`): `{value,label,variant,icon}`.
 *
 * Era spezzato in due — questa mappa, con **hex inline** e glifi FA4
 * (`times`, `file-alt`) entrambi vietati dalle convenzioni, più un
 * `VEHICLE_STATUS_VARIANT` che viveva in `vehicleListParts` e doveva restare
 * allineato a mano.
 *
 * `operational: false` = il mezzo non fa servizio. Distinto da `deleted`: un
 * mezzo venduto o dismesso esce dalla flotta ma resta nello storico dei costi,
 * dei danni e dei verbali.
 *
 * Le `label` restano le chiavi i18n storiche (`available`, `maintenance`, …):
 * sono già tradotte e usate dai consumer via `i18n.t(entry.label)`.
 */
export const VEHICLE_STATUS = [
  {
    value: 'available',
    label: 'available',
    variant: 'success',
    icon: 'check',
    operational: true,
    order: 1,
  },
  {
    value: 'maintenance',
    label: 'maintenance',
    variant: 'warning',
    icon: 'wrench',
    operational: false,
    order: 2,
  },
  {
    value: 'unavailable',
    label: 'unavailable',
    variant: 'danger',
    icon: 'xmark',
    operational: false,
    order: 3,
  },
  {
    value: 'wait_for_documents',
    label: 'wait_for_documents',
    variant: 'info',
    icon: 'file-lines',
    operational: false,
    order: 4,
  },
  {
    value: 'lost',
    label: 'lost',
    variant: 'neutral',
    icon: 'circle-question',
    operational: false,
    order: 5,
  },
  {
    value: 'decommissioned',
    label: 'vehicle_status_decommissioned',
    variant: 'neutral',
    icon: 'box-archive',
    operational: false,
    order: 6,
  },
  {
    value: 'sold',
    label: 'vehicle_status_sold',
    variant: 'neutral',
    icon: 'hand-holding-dollar',
    operational: false,
    order: 7,
  },
];

export const vehicleStatusMap = Object.fromEntries(VEHICLE_STATUS.map((s) => [s.value, s]));

export const DEFAULT_VEHICLE_STATUS = 'available';

export const isVehicleStatus = (value) => Boolean(vehicleStatusMap[value]);

/** Stato normalizzato: assente ⇒ `available` (nessun backfill sulle anagrafiche). */
export const vehicleStatus = (vehicle) =>
  isVehicleStatus(vehicle?.status) ? vehicle.status : DEFAULT_VEHICLE_STATUS;

/** Il mezzo è ancora in flotta: dismessi e venduti escono dai conteggi. */
export const isInFleet = (vehicle) =>
  !vehicle?.deleted && !['decommissioned', 'sold'].includes(vehicleStatus(vehicle));

/**
 * Il mezzo può fare servizio.
 *
 * Unifica i tre concetti che finora vivevano scollegati e che nessuno metteva
 * insieme: lo stato dichiarato, un danno **bloccante** aperto
 * (`damagesLogic.isBlocking`, finora consumato solo per un contatore) e il
 * censimento scaduto. `assigned` NON entra qui: è occupazione della giornata,
 * non indisponibilità.
 *
 * `blockingDamages` va passato dal chiamante — questo modulo non conosce il
 * dominio danni, e riceverlo evita di trascinarci dentro una dipendenza.
 */
export function isVehicleAvailable(vehicle, { hasBlockingDamage = false } = {}) {
  if (!vehicle) return false;
  if (!vehicleStatusMap[vehicleStatus(vehicle)]?.operational) return false;
  return !hasBlockingDamage;
}

// Dotazioni del veicolo → chip caratteristiche (icona FontAwesome + label i18n).
export const vehicleEquipmentMap = {
  wheelchair: { label: 'carrozzina', icon: 'wheelchair' },
  step: { label: 'scalino', icon: 'shoe-prints' },
  pocket_mask: { label: 'pocket_mask', icon: 'kit-medical' },
  cable_22kw: { label: 'cavo_22kw', icon: 'plug' },
  policlinico_key: { label: 'chiave_poli', icon: 'key' },
};

// Documenti con scadenza monitorata (per le chip di allerta).
// `data_omologazione` è esclusa di proposito: è la data in cui il mezzo è stato
// omologato, non una scadenza. Il controllo periodico annuale è un intervento
// ricorrente (`periodic_check`), e come tale sta nello scadenzario.
export const VEHICLE_EXPIRY_DOCS = [
  { field: 'data_revisione', label: 'revisione' },
  { field: 'data_assicurazione', label: 'assicurazione' },
];

// Soglia (giorni) entro cui una scadenza è considerata "imminente".
const EXPIRY_WARNING_DAYS = 30;

// Stato di scadenza di una data documento: null se assente.
// Ritorna { variant, expired, days } dove days è negativo se già scaduta.
export const getDocExpiryStatus = (value) => {
  if (!value) return null;
  const days = toMoment(value).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (days < 0) return { variant: 'danger', expired: true, days };
  if (days <= EXPIRY_WARNING_DAYS) return { variant: 'warning', expired: false, days };
  return { variant: 'success', expired: false, days };
};

// Testo relativo alla scadenza (oggi / domani / tra N giorni / scaduta da N giorni).
const expiryTiming = (days) => {
  if (days < 0) return i18next.t('scaduta_da_giorni', { days: -days });
  if (days === 0) return i18next.t('scade_oggi');
  if (days === 1) return i18next.t('scade_domani');
  return i18next.t('scade_tra_giorni', { days });
};

// Chip di allerta scadenze: solo per documenti scaduti o in scadenza (≤30gg).
export const getVehicleExpiryChips = (vehicle = {}) =>
  VEHICLE_EXPIRY_DOCS.map(({ field, label }) => {
    const status = getDocExpiryStatus(vehicle[field]);
    if (!status || status.variant === 'success') return null;
    return {
      key: field,
      variant: status.variant,
      icon: status.expired ? 'triangle-exclamation' : 'clock',
      label: `${i18next.t(label)} · ${expiryTiming(status.days)}`,
    };
  }).filter(Boolean);

// Chip dotazioni: una chip per ogni dotazione presente sul veicolo.
export const getVehicleEquipmentChips = (vehicle = {}) =>
  Object.entries(vehicleEquipmentMap)
    .filter(([key]) => Boolean(vehicle[key]))
    .map(([key, cfg]) => ({
      key,
      icon: cfg.icon,
      variant: 'neutral',
      label: i18next.t(cfg.label),
    }));

// Set completo di chip per le card (allerte scadenze prima, poi dotazioni).
export const getVehicleChips = (vehicle = {}) => [
  ...getVehicleExpiryChips(vehicle),
  ...getVehicleEquipmentChips(vehicle),
];
